import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useStore } from '../../hooks/useStore';
import { fetchProductDiff, optimizeProduct, resetProduct } from '../../lib/api';
import type { DiffField, DiffStatus } from '../../types';
import { safeArray } from '../../lib/safe';

export const Route = createFileRoute('/products/$productId')({
  component: ProductDetailPage,
});

// ── Status badge colors ────────────────────────────────
const STATUS_STYLES: Record<DiffStatus, string> = {
  changed: 'bg-blue-50 text-blue-700 border-blue-200',
  preserved: 'bg-slate-50 text-slate-600 border-slate-200',
  missing_original: 'bg-amber-50 text-amber-700 border-amber-200',
  unoptimized: 'bg-slate-50 text-slate-500 border-slate-200',
};

const STATUS_LABELS: Record<DiffStatus, string> = {
  changed: 'Changed',
  preserved: 'Unchanged',
  missing_original: 'No Original',
  unoptimized: 'Not Optimized',
};

// ── Diff Row ────────────────────────────────────────────
function DiffRow({ field }: { field: DiffField }) {
  const [expanded, setExpanded] = useState(false);
  const isLongText = (field.original?.length ?? 0) > 100 || field.current.length > 100;

  return (
    <div className="border border-[var(--ccb-border)] rounded-lg overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 bg-white cursor-pointer hover:bg-slate-50/50 transition-colors"
        onClick={() => isLongText && setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <span className="font-medium text-sm text-[var(--ccb-text)]">{field.label}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLES[field.status]}`}>
            {STATUS_LABELS[field.status]}
          </span>
        </div>
        {isLongText && (
          <span className="text-xs text-[var(--ccb-text-secondary)]">{expanded ? 'Collapse' : 'Expand'}</span>
        )}
      </div>

      {/* Values */}
      <div className={`grid grid-cols-2 divide-x divide-[var(--ccb-border)] border-t border-[var(--ccb-border)] ${!expanded && isLongText ? 'max-h-24 overflow-hidden' : ''}`}>
        {/* Original */}
        <div className="p-4">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-[var(--ccb-text-secondary)] block mb-1.5">Original</span>
          {field.original ? (
            <p className="text-sm text-[var(--ccb-text)] whitespace-pre-wrap break-words">{field.original}</p>
          ) : (
            <p className="text-sm text-slate-400 italic">Not stored</p>
          )}
        </div>
        {/* Current */}
        <div className="p-4">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-[var(--ccb-text-secondary)] block mb-1.5">Current</span>
          <p className="text-sm text-[var(--ccb-text)] whitespace-pre-wrap break-words">{field.current}</p>
        </div>
      </div>
    </div>
  );
}

// ── Product Detail Page ─────────────────────────────────
function ProductDetailPage() {
  const { productId } = Route.useParams();
  const { activeStore } = useStore();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['product-diff', activeStore.shop, productId],
    queryFn: () => fetchProductDiff(activeStore.shop, productId),
  });

  const optimizeMutation = useMutation({
    mutationFn: (force: boolean) => optimizeProduct(activeStore.shop, productId, force),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['product-diff', activeStore.shop, productId] }),
  });

  const resetMutation = useMutation({
    mutationFn: (mode: 'clear' | 'revert') => resetProduct(activeStore.shop, productId, mode),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['product-diff', activeStore.shop, productId] }),
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 bg-slate-100 rounded animate-pulse" />
        <div className="h-64 bg-slate-100 rounded-xl animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <p className="text-red-700 font-medium">Failed to load product diff</p>
        <p className="text-sm text-red-600 mt-1">{(error as Error).message}</p>
      </div>
    );
  }

  const product = data?.product;
  const diff = safeArray(data?.diff);
  const recommendations = safeArray(data?.recommendations);

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-[var(--ccb-text-secondary)]">
        <Link to="/products" className="hover:text-[var(--ccb-primary)] transition-colors">Products</Link>
        <span>/</span>
        <span className="text-[var(--ccb-text)] font-medium truncate">{product?.title ?? productId}</span>
      </div>

      {/* Product header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          {product?.image && (
            <img src={product.image} alt="" className="w-16 h-16 rounded-lg object-cover border border-[var(--ccb-border)]" />
          )}
          <div>
            <h2 className="text-xl font-semibold text-[var(--ccb-text)]">{product?.title}</h2>
            <div className="flex items-center gap-2 mt-1 text-sm text-[var(--ccb-text-secondary)]">
              <span>{product?.vendor}</span>
              <span className="text-slate-300">|</span>
              <span className={product?.isOptimized ? 'text-blue-600' : 'text-amber-600'}>
                {product?.isOptimized ? 'Optimized' : 'Not Optimized'}
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => optimizeMutation.mutate(false)}
            disabled={optimizeMutation.isPending}
            className="bg-[var(--ccb-primary)] text-white text-sm font-medium px-4 py-2 rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {optimizeMutation.isPending ? 'Optimizing...' : product?.isOptimized ? 'Re-Optimize' : 'Optimize'}
          </button>
          {product?.isOptimized && (
            <>
              <button
                onClick={() => resetMutation.mutate('revert')}
                disabled={resetMutation.isPending}
                className="border border-[var(--ccb-border)] text-sm font-medium px-4 py-2 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
              >
                Revert to Original
              </button>
              <button
                onClick={() => resetMutation.mutate('clear')}
                disabled={resetMutation.isPending}
                className="border border-red-200 text-red-600 text-sm font-medium px-4 py-2 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
              >
                Clear Originals
              </button>
            </>
          )}
        </div>
      </div>

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-blue-800 mb-2">Recommendations</h3>
          <ul className="space-y-1">
            {recommendations.map((r, i) => (
              <li key={i} className="text-sm text-blue-700 flex items-start gap-2">
                <span className="text-blue-400 mt-0.5">•</span>
                {r}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Diff Fields */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-[var(--ccb-text)]">Field Comparison</h3>
        {diff.map((f) => (
          <DiffRow key={f.field} field={f} />
        ))}
        {diff.length === 0 && (
          <p className="text-sm text-[var(--ccb-text-secondary)] p-4">No comparison data available</p>
        )}
      </div>
    </div>
  );
}
